import React from 'react';
import './SectionIcon.css';

// Hand-drawn section marks. Two strokes only: `si-line` carries the drawing,
// `si-accent` picks out the one detail that names the section — that split is
// what keeps six different drawings reading as one set.
//
// Each drawing is authored freehand on a 48-unit grid, so their natural sizes
// don't match: the book fills 34 units, the steps 39. Left as authored they
// look like six icons at five different sizes. So each shape declares the
// bounding box it actually occupies, and the component below scales and
// centres it to one common size — nobody has to redraw a path to keep the set
// aligned.

// Side of the square every drawing is fitted into, inside the 48-unit grid.
const ART_SIZE = 36;
const GRID = 48;

// Stroke weight after fitting. Each drawing counter-scales this so a shape
// that had to be scaled up doesn't come out with fatter lines than its
// neighbours.
const STROKE = 2;

const shapes = {
  // An open book, spine down the middle, with a ribbon marking a page: the
  // catalogue. Drawn open rather than stacked — a closed stack reads as a box
  // at small sizes.
  courses: {
    box: [7, 9.9, 34, 29.6],
    art: (
      <>
        <path className="si-line" d="M24 14.5 C19.5 11 13.5 9.5 7 10 V35 C13.5 34.5 19.5 36 24 39.5 Z" />
        <path className="si-line" d="M24 14.5 C28.5 11 34.5 9.5 41 10 V35 C34.5 34.5 28.5 36 24 39.5 Z" />
        <path className="si-accent" d="M24 14.5 V39.5" />
        <path className="si-accent" d="M32.5 10.6 V24 L36 21.4 L39.5 24 V10.1" />
      </>
    ),
  },

  // A person in front of a board: the teacher, not a generic avatar.
  //
  // Everything centres on x=24 -- the board, the two lines of writing, the head
  // and the shoulders. Ragged-left writing leaves the board's right half empty
  // and tips the whole drawing sideways.
  //
  // Vertically the three pieces are stacked with an even four units between
  // them, twice the stroke, so a clear two units of background shows through.
  // Nothing here is filled, so a head set any closer doesn't sit on the
  // shoulders -- the shoulder curve simply runs across the chin.
  teachers: {
    box: [7, 4, 34, 39],
    art: (
      <>
        <rect className="si-line" x="7" y="4" width="34" height="16" rx="2.5" />
        <path className="si-accent" d="M14 9.5 H34 M17.5 14.5 H30.5" />
        <circle className="si-line" cx="24" cy="28" r="4" />
        <path className="si-line" d="M15.5 43 C16.5 38.5 20 36 24 36 C28 36 31.5 38.5 32.5 43" />
      </>
    ),
  },

  // Two coins, a dollar on the front one and a euro on the one behind it:
  // prices. The two rims are kept fully apart — overlapped without a fill to
  // hide behind, they merge into one smudged circle, and a partial arc reads
  // as a spinner rather than a coin.
  // Both coins are the same circle on a 45-degree diagonal: unequal radii read
  // as a drawing mistake rather than as perspective, since neither coin is
  // filled to sit in front of the other. The gap is what separates them, so it
  // has to clear both strokes.
  prices: {
    box: [8, 8, 32, 32],
    art: (
      <>
        <circle className="si-line" cx="31.5" cy="16.5" r="8.5" />
        {/* Euro: a C opening to the right, crossed by its two bars. */}
        <path className="si-accent" d="M35.5 13.2 A5.2 5.2 0 1 0 35.5 19.8" />
        <path className="si-accent" d="M25.3 14.8 H33.6 M25.3 18.2 H32.6" />
        <circle className="si-line" cx="16.5" cy="31.5" r="8.5" />
        <path className="si-accent" d="M16.5 25.9 V37.1" />
        <path
          className="si-accent"
          d="M19.1 28.5 C19.1 27.3 17.9 26.6 16.5 26.6 C15.1 26.6 13.9 27.3 13.9 28.7 C13.9 31.8 19.5 30.6 19.5 34 C19.5 35.4 18.1 36.4 16.5 36.4 C14.9 36.4 13.6 35.6 13.6 34.1"
        />
      </>
    ),
  },

  // Two speech bubbles, the front one carrying a star: reviews.
  feedback: {
    box: [5, 5, 39, 36],
    art: (
      <>
        <path className="si-line" d="M31 5 H41 A3 3 0 0 1 44 8 V16 A3 3 0 0 1 41 19 H31 A3 3 0 0 1 28 16 V8 A3 3 0 0 1 31 5 Z" />
        <path
          className="si-line"
          d="M8 21 H27 A3 3 0 0 1 30 24 V33 A3 3 0 0 1 27 36 H16 L10 41 V36 H8 A3 3 0 0 1 5 33 V24 A3 3 0 0 1 8 21 Z"
        />
        <path
          className="si-accent"
          d="M17.5 22 L19.2 26.2 L23.7 26.5 L20.3 29.4 L21.3 33.8 L17.5 31.4 L13.7 33.8 L14.7 29.4 L11.3 26.5 L15.8 26.2 Z"
        />
      </>
    ),
  },

  // Three rising steps with a flag on top: how it works.
  how: {
    box: [5, 7, 39, 33],
    art: (
      <>
        <path className="si-line" d="M5 40 H15 V31 H25 V22 H35 V40 H43" />
        <path className="si-accent" d="M35 22 V7" />
        <path className="si-accent" d="M35 8.5 L44 12 L35 15.5 Z" />
      </>
    ),
  },

  // Question mark inside a rounded frame with a dog-ear: FAQ.
  faq: {
    box: [6, 6, 33, 36],
    art: (
      <>
        <path className="si-line" d="M9 6 H31 L39 14 V39 A3 3 0 0 1 36 42 H9 A3 3 0 0 1 6 39 V9 A3 3 0 0 1 9 6 Z" />
        <path className="si-line" d="M31 6 V14 H39" />
        <path
          className="si-accent"
          d="M17.5 21.5 C17.5 18 20 16 22.5 16 C25.5 16 27.8 18 27.8 21 C27.8 25 22.8 25.5 22.8 29.5"
        />
        <circle className="si-accent" cx="22.8" cy="34.5" r="1.8" />
      </>
    ),
  },
};

const round = n => Math.round(n * 1000) / 1000;

export default function SectionIcon({ name, className = '' }) {
  const shape = shapes[name];
  if (!shape) return null;

  // Fit the drawing's own box into ART_SIZE by its longer side, then move its
  // centre onto the centre of the grid.
  const [x, y, w, h] = shape.box;
  const scale = ART_SIZE / Math.max(w, h);
  const tx = round(GRID / 2 - scale * (x + w / 2));
  const ty = round(GRID / 2 - scale * (y + h / 2));

  return (
    <svg
      className={`section-icon ${className}`}
      viewBox={`0 0 ${GRID} ${GRID}`}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <g transform={`translate(${tx} ${ty}) scale(${round(scale)})`} strokeWidth={round(STROKE / scale)}>
        {shape.art}
      </g>
    </svg>
  );
}
